import { useCloseAccountingPeriodMutation } from '@api/accounting-and-finance/accounting.period.api.rtk';
import useAppContext from '@hooks/useAppContext';
import { useCurrentModule } from '@hooks/useCurrentModule';
import useDisclosure from '@hooks/useDisclosure';
import useToast from '@hooks/useToast';
import { IAccountingPeriodResponseData } from '@modules/accounting-and-finance/interface/accounting-period/accounting-period-interface';
import { CloseOutlined, InfoOutlined } from '@mui/icons-material';
import { Button, Collapse, IconButton, Stack, Typography } from '@mui/material';
import { useAppSelector } from '@store/index';
import moment from 'moment';
import { useEffect, useMemo } from 'react';

const ACCGAccountPeriodWarning = () => {
  const { toast } = useToast();
  const { openSecCodeDialog } = useAppContext();
  const currentModule = useCurrentModule();

  const { accountingPeriodID } = useAppSelector(
    (state) => state.selectedAccountingPeriod.selectedAccountingPeriod,
  );

  const selectedAccountingPeriod = useAppSelector(
    (state) => state.accgAccountingPeriod,
  )
    ?.filter((period) => period.isActive && !period.isClosed)
    ?.find((item) => item.accountingPeriodID === accountingPeriodID);

  const { showWarning, isGracePeriodOver, totalDays } = useMemo(() => {
    let showWarning = false;
    let totalDays = 0;
    let isGracePeriodOver = false;

    if (!selectedAccountingPeriod?.periodEnd)
      return { showWarning, totalDays, isGracePeriodOver };

    const periodEnd = moment(selectedAccountingPeriod.periodEnd);
    const graceEnd = periodEnd.clone().add(1, 'month').endOf('month');
    const today = moment();

    if (!selectedAccountingPeriod?.isClosed && currentModule === 'ABK') {
      if (today.isAfter(periodEnd, 'day')) {
        showWarning = true;
        totalDays = today.diff(periodEnd, 'days');
        isGracePeriodOver = today.isAfter(graceEnd, 'day');
      }
    }

    return { showWarning, totalDays, isGracePeriodOver };
  }, [selectedAccountingPeriod, currentModule]);

  const warningDisclosure = useDisclosure();

  const [closeAccountingPeriodMutation] = useCloseAccountingPeriodMutation();

  const closeAccountingPeriod = async () => {
    openSecCodeDialog(async (res) => {
      if (!res?.data.isValid) return;
      try {
        const [responseData]: IAccountingPeriodResponseData[] =
          await closeAccountingPeriodMutation(`${accountingPeriodID}`).unwrap();

        const { responseCode, responseMessage } = responseData;
        const severity = responseCode === 200 ? 'success' : 'warning';

        toast({
          severity,
          message: responseMessage,
        });
      } catch (err) {
        toast({
          severity: 'error',
          message:
            'An unexpected error occurred while closing the accounting period.',
        });
      }
    }, 'setup_control_bk');
  };

  useEffect(() => {
    if (!showWarning) return warningDisclosure.onClose();

    warningDisclosure.onOpen();
  }, [showWarning]);

  return (
    <Collapse in={warningDisclosure.isOpen} unmountOnExit>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent='space-between'
        sx={{
          bgcolor: 'secondary.main',
          height: { xs: 'auto', sm: 50 },
          position: 'relative',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems='center'
          gap={1}
          sx={{
            color: 'secondary.contrastText',
            py: 1,
            flex: 1,
            justifyContent: 'center',
          }}
        >
          <InfoOutlined />
          {isGracePeriodOver ? (
            <Typography
              fontSize={{ xs: 12, sm: 14 }}
              sx={{ textWrap: { xs: 'wrap', sm: 'nowrap' } }}
            >
              <b>{selectedAccountingPeriod?.periodName}</b> accounting period is
              still open.
            </Typography>
          ) : (
            <Typography
              fontSize={{ xs: 12, sm: 14 }}
              sx={{ textWrap: { xs: 'wrap', sm: 'nowrap' } }}
            >
              You have <b>{totalDays}</b> days left to close the accounting
              period.
            </Typography>
          )}
          <Button
            variant='outlined'
            sx={{
              borderColor: 'secondary.contrastText',
              color: 'secondary.contrastText',
              minHeight: 0,
              py: 2 / 8,
              px: { xs: 2, sm: 10 / 8 },
              flexShrink: 0,
              ':hover': {
                bgcolor: 'secondary.contrastText',
                color: 'secondary.main',
                borderColor: 'secondary.contrastText',
              },
            }}
            onClick={closeAccountingPeriod}
          >
            <Typography noWrap>Click here to close it</Typography>
          </Button>
        </Stack>
        <IconButton
          sx={{
            bgcolor: 'secondary.main',
            color: 'secondary.contrastText',
            position: { xs: 'absolute', sm: 'relative' },
            top: 0,
            right: 0,
          }}
          onClick={warningDisclosure.onClose}
        >
          <CloseOutlined />
        </IconButton>
      </Stack>
    </Collapse>
  );
};

export default ACCGAccountPeriodWarning;
